import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getApi } from '../../utils/api';

export const COLLEGE_LABELS = {
    expert: 'Expert·e',
    occupant: 'Occupant·e',
    salarie: 'Salarié·e',
    partenaire: 'Partenaire',
    soutien_financier: 'Soutien financier'
};

export const COLLEGE_COLORS = {
    expert: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
    occupant: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300' },
    salarie: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-300' },
    partenaire: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
    soutien_financier: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' }
};

export const STATUS_LABELS = {
    pending: 'En attente',
    sent: 'Envoyée',
    reminded: 'Rappel envoyé',
    used: 'Compte créé',
    expired: 'Expirée'
};

export const STATUS_COLORS = {
    pending: 'bg-gray-100 text-gray-700',
    sent: 'bg-blue-100 text-blue-700',
    reminded: 'bg-yellow-100 text-yellow-700',
    used: 'bg-green-100 text-green-700',
    expired: 'bg-red-100 text-red-700'
};

// Prévisualisation CSV
export const previewCSV = createAsyncThunk('invitations/preview', async (file, { rejectWithValue }) => {
    try {
        const api = await getApi();
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/invitations/preview', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Erreur lors de l\'analyse du fichier');
    }
});

// Import et envoi des invitations
export const importInvitations = createAsyncThunk('invitations/import', async (rows, { rejectWithValue }) => {
    try {
        const api = await getApi();
        const response = await api.post('/invitations/import', { rows });
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Erreur lors de l\'import');
    }
});

// Récupérer la liste
export const fetchInvitations = createAsyncThunk('invitations/fetchAll', async (filters = {}, { rejectWithValue }) => {
    try {
        const api = await getApi();
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.college) params.append('college', filters.college);
        if (filters.search) params.append('search', filters.search);
        if (filters.page) params.append('page', filters.page);
        const response = await api.get(`/invitations?${params}`);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Erreur lors du chargement');
    }
});

// Statistiques
export const fetchInvitationStats = createAsyncThunk('invitations/stats', async (_, { rejectWithValue }) => {
    try {
        const api = await getApi();
        const response = await api.get('/invitations/stats');
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Erreur');
    }
});

// Mettre à jour une invitation
export const updateInvitation = createAsyncThunk('invitations/update', async ({ id, data }, { rejectWithValue }) => {
    try {
        const api = await getApi();
        const response = await api.put(`/invitations/${id}`, data);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Erreur lors de la mise à jour');
    }
});

// Renvoyer une invitation
export const resendInvitation = createAsyncThunk('invitations/resend', async (id, { rejectWithValue }) => {
    try {
        const api = await getApi();
        const response = await api.post(`/invitations/${id}/resend`);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Erreur lors du renvoi');
    }
});

// Révoquer une invitation
export const revokeInvitation = createAsyncThunk('invitations/revoke', async (id, { rejectWithValue }) => {
    try {
        const api = await getApi();
        await api.delete(`/invitations/${id}`);
        return { id };
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Erreur lors de la révocation');
    }
});

// Valider un token (public)
export const validateInvitationToken = createAsyncThunk('invitations/validateToken', async (token, { rejectWithValue }) => {
    try {
        const api = await getApi();
        const response = await api.get(`/invitations/validate/${token}`);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Lien invalide ou expiré');
    }
});

const initialState = {
    invitations: [],
    stats: null,
    preview: null,
    tokenData: null,
    pagination: { page: 1, total: 0, totalPages: 0 },
    isLoading: false,
    isImporting: false,
    error: null
};

const invitationSlice = createSlice({
    name: 'invitations',
    initialState,
    reducers: {
        clearPreview: (state) => { state.preview = null; },
        clearError: (state) => { state.error = null; },
        clearTokenData: (state) => { state.tokenData = null; },
        updateLocalInvitation: (state, action) => {
            const idx = state.invitations.findIndex(i => i._id === action.payload._id);
            if (idx !== -1) state.invitations[idx] = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(previewCSV.pending, (state) => { state.isLoading = true; state.error = null; state.preview = null; })
            .addCase(previewCSV.fulfilled, (state, action) => { state.isLoading = false; state.preview = action.payload.data; })
            .addCase(previewCSV.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })

            .addCase(importInvitations.pending, (state) => { state.isImporting = true; state.error = null; })
            .addCase(importInvitations.fulfilled, (state) => { state.isImporting = false; state.preview = null; })
            .addCase(importInvitations.rejected, (state, action) => { state.isImporting = false; state.error = action.payload; })

            .addCase(fetchInvitations.pending, (state) => { state.isLoading = true; })
            .addCase(fetchInvitations.fulfilled, (state, action) => {
                state.isLoading = false;
                state.invitations = action.payload.data;
                state.pagination = action.payload.pagination;
            })
            .addCase(fetchInvitations.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })

            .addCase(fetchInvitationStats.fulfilled, (state, action) => { state.stats = action.payload.data; })

            .addCase(updateInvitation.fulfilled, (state, action) => {
                const updated = action.payload.data;
                const idx = state.invitations.findIndex(i => i._id === updated._id);
                if (idx !== -1) state.invitations[idx] = updated;
            })

            .addCase(resendInvitation.fulfilled, (state, action) => {
                const updated = action.payload.data;
                const idx = state.invitations.findIndex(i => i._id === updated._id);
                if (idx !== -1) state.invitations[idx] = updated;
            })

            .addCase(revokeInvitation.fulfilled, (state, action) => {
                state.invitations = state.invitations.filter(i => i._id !== action.payload.id);
            })

            .addCase(validateInvitationToken.pending, (state) => { state.isLoading = true; state.error = null; })
            .addCase(validateInvitationToken.fulfilled, (state, action) => { state.isLoading = false; state.tokenData = action.payload.data; })
            .addCase(validateInvitationToken.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; });
    }
});

export const { clearPreview, clearError, clearTokenData } = invitationSlice.actions;
export default invitationSlice.reducer;
